/**
 * CalibrationPane — the calibration ledger UI (v0.5.6 AE-030).
 *
 * Renders GET /calibration: Brier score, hit rate by conviction bucket,
 * scenario hit rate, per-track attribution, drift alerts, and the raw
 * resolved-decision entries. Anti-hallucination: displays exactly what
 * the backend computed; "insufficient data" is shown honestly instead
 * of a zero-filled chart.
 */
import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { Sparkline } from './viz/Sparkline';

const WINDOWS = [30, 90, 365] as const;

/** v0.5.8 AE-107: weekly hit-rate trend computed from the resolved
 * entries the ledger already returns (outcome_date + actual_return_pct).
 * Weeks with no resolutions are skipped, not zero-filled; fewer than two
 * buckets renders nothing. */
export function weeklyHitRates(
  entries: { outcome_date: string; actual_return_pct: number }[],
): number[] {
  const byWeek = new Map<string, { wins: number; total: number }>();
  for (const e of entries) {
    if (!e.outcome_date) continue;
    const d = new Date(`${e.outcome_date}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) continue;
    // ISO-ish weekly bucket: the Monday of the entry's week (UTC).
    const day = d.getUTCDay();
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - ((day + 6) % 7));
    const key = monday.toISOString().slice(0, 10);
    const b = byWeek.get(key) ?? { wins: 0, total: 0 };
    b.total += 1;
    if (e.actual_return_pct > 0) b.wins += 1;
    byWeek.set(key, b);
  }
  return [...byWeek.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, b]) => (100 * b.wins) / b.total);
}

export function CalibrationPane() {
  const [windowDays, setWindowDays] = useState<number>(90);
  const [ledger, setLedger] = useState<api.CalibrationLedger | null>(null);
  const [scorecard, setScorecard] = useState<api.StrategyScorecardRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api.calibration
      .get(windowDays)
      .then((l) => { if (alive) setLedger(l); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [windowDays]);

  useEffect(() => {
    let alive = true;
    api.calibration
      .strategies()
      .then((r) => { if (alive) setScorecard(r.strategies); })
      .catch(() => { if (alive) setScorecard(null); }); // scorecard is additive; ledger errors surface above
    return () => { alive = false; };
  }, []);

  const snap = ledger?.snapshot ?? null;

  // v0.5.8 AE-107: direction over time, from the same entries — no new fetch.
  const hitTrend = useMemo(
    () => weeklyHitRates(ledger?.entries ?? []),
    [ledger],
  );

  return (
    <div className="space-y-4" data-testid="calibration-pane">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Calibration ledger</h3>
          <p className="text-[11px] text-ink-mute mt-0.5">
            How well conviction predicts outcomes. Entries are seeded on fill and
            resolved during reconcile.
          </p>
        </div>
        <div className="flex rounded overflow-hidden border border-edge" role="tablist">
          {WINDOWS.map((w) => (
            <button
              key={w}
              role="tab"
              aria-selected={windowDays === w}
              data-testid={`calibration-window-${w}`}
              onClick={() => setWindowDays(w)}
              className={`text-xs px-2.5 py-1 ${
                windowDays === w ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {err && <div className="card p-3 text-xs text-warn border-warn">{err}</div>}
      {loading && !ledger && <div className="card p-4 text-xs text-ink-mute">Loading calibration…</div>}

      {snap && snap.entries_count < 5 && (
        <div className="card p-4 text-sm text-ink-mute" data-testid="calibration-insufficient">
          Insufficient data: {snap.entries_count} resolved decision(s) in the last{' '}
          {snap.window_days} days (needs ≥ 5 for meaningful calibration). Metrics
          below are shown as-is, not padded.
        </div>
      )}

      {snap && snap.drift_alerts.length > 0 && (
        <div className="card p-3 border-warn" data-testid="calibration-alerts">
          <div className="text-xs text-warn font-semibold mb-1">Drift alerts</div>
          <ul className="space-y-0.5">
            {snap.drift_alerts.map((a, i) => (
              <li key={i} className="text-xs text-warn">⚠ {a}</li>
            ))}
          </ul>
        </div>
      )}

      {snap && (
        <div className="grid gap-3 sm:grid-cols-3" data-testid="calibration-metrics">
          <MetricCard
            label="Brier score"
            value={snap.brier_score_overall != null ? snap.brier_score_overall.toFixed(3) : '—'}
            hint="0 = perfect, 0.25 = random; lower is better"
            accent={
              snap.brier_score_overall == null
                ? undefined
                : snap.brier_score_overall < 0.25
                  ? 'ok'
                  : 'warn'
            }
          />
          <MetricCard
            label="Scenario hit rate"
            value={snap.scenario_hit_rate != null ? `${(snap.scenario_hit_rate * 100).toFixed(0)}%` : '—'}
            hint="outcomes landing in the predicted scenario"
            extra={
              hitTrend.length >= 2 ? (
                <span data-testid="hit-rate-trend" title="weekly hit rate over the window">
                  <Sparkline values={hitTrend} width={80} height={20} tone="accent" area />
                </span>
              ) : null
            }
          />
          <MetricCard
            label="Resolved decisions"
            value={`${snap.entries_count}`}
            hint={`last ${snap.window_days} days`}
          />
        </div>
      )}

      {snap && Object.keys(snap.conviction_calibration).length > 0 && (
        <div className="card p-4" data-testid="calibration-buckets">
          <div className="text-xs font-semibold mb-2">Hit rate by conviction</div>
          <div className="space-y-2">
            {(['1-3', '4-6', '7-10'] as const)
              .filter((b) => snap.conviction_calibration[b])
              .map((b) => {
                const m = snap.conviction_calibration[b];
                const hit = m.hit_rate ?? 0;
                const target = b === '7-10' ? 0.7 : null;
                return (
                  <div key={b} className="flex items-center gap-3">
                    <span className="text-[11px] text-ink-mute w-20 shrink-0">conviction {b}</span>
                    <div className="flex-1 h-3 bg-bg-elev rounded overflow-hidden relative">
                      <div
                        className={`h-full ${target != null && hit < target ? 'bg-warn' : 'bg-ok'}`}
                        style={{ width: `${Math.min(100, hit * 100)}%` }}
                      />
                      {target != null && (
                        <div
                          className="absolute top-0 h-full w-px bg-ink"
                          style={{ left: `${target * 100}%` }}
                          title="target 70%"
                        />
                      )}
                    </div>
                    <span className="num text-xs w-28 text-right">
                      {(hit * 100).toFixed(0)}% · {m.count} · {m.avg_return_pct >= 0 ? '+' : ''}
                      {m.avg_return_pct?.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
          </div>
          <div className="text-[10px] text-ink-mute mt-2">
            hit rate · decisions · avg return. Well-calibrated 7-10 conviction should hit ≥ 70% (marker).
          </div>
        </div>
      )}

      {snap && Object.keys(snap.track_attribution).length > 0 && (
        <div className="card p-4" data-testid="calibration-tracks">
          <div className="text-xs font-semibold mb-2">Track attribution (avg contribution)</div>
          <div className="grid gap-2 sm:grid-cols-3">
            {Object.entries(snap.track_attribution).map(([track, alpha]) => (
              <div key={track} className="flex items-baseline justify-between text-xs">
                <span className="text-ink-mute">{track}</span>
                <span className={`num ${alpha >= 0 ? 'text-ok' : 'text-bad'}`}>
                  {alpha >= 0 ? '+' : ''}{alpha.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {scorecard && scorecard.length > 0 && (
        <div className="card p-4" data-testid="calibration-strategy-scorecard">
          <div className="text-xs font-semibold mb-1">Strategy scorecard</div>
          <p className="text-[10px] text-ink-mute mb-2">
            Every proposal a strategy has ever made, how you decided it, and how it
            worked out — rejected ideas are scored too.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-ink-mute text-left">
                <tr>
                  <th className="pb-1">Strategy</th>
                  <th className="pb-1 text-right">Proposed</th>
                  <th className="pb-1 text-right">Approved</th>
                  <th className="pb-1 text-right">Scored</th>
                  <th className="pb-1 text-right">Hit rate</th>
                  <th className="pb-1 text-right">Avg outcome</th>
                  <th className="pb-1 text-right">Outcomes</th>
                </tr>
              </thead>
              <tbody>
                {scorecard.map((s) => {
                  const approved =
                    (s.order_status_counts['approved'] ?? 0) +
                    (s.order_status_counts['filled'] ?? 0);
                  return (
                    <tr key={s.strategy_id} className="border-t border-edge" data-testid={`scorecard-${s.strategy_id}`}>
                      <td className="py-1 font-semibold">{s.name}</td>
                      <td className="py-1 text-right num">{s.proposed}</td>
                      <td className="py-1 text-right num">{approved}</td>
                      <td className="py-1 text-right num">{s.scored}</td>
                      <td className="py-1 text-right num">
                        {s.hit_rate_pct != null ? `${s.hit_rate_pct.toFixed(0)}%` : '—'}
                      </td>
                      <td className={`py-1 text-right num ${
                        s.avg_outcome_return_pct == null ? '' : s.avg_outcome_return_pct >= 0 ? 'text-ok' : 'text-bad'
                      }`}>
                        {s.avg_outcome_return_pct != null
                          ? `${s.avg_outcome_return_pct >= 0 ? '+' : ''}${s.avg_outcome_return_pct.toFixed(1)}%`
                          : '—'}
                      </td>
                      <td className="py-1 text-right" data-testid={`scorecard-outcomes-${s.strategy_id}`}>
                        {(s.outcome_returns_pct?.length ?? 0) > 0 ? (
                          <OutcomeStrip returns={s.outcome_returns_pct} />
                        ) : (
                          <span className="text-ink-mute">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ledger && ledger.entries.length > 0 && (
        <div className="card p-4" data-testid="calibration-entries">
          <div className="text-xs font-semibold mb-2">Resolved entries ({ledger.entries.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-ink-mute text-left">
                <tr>
                  <th className="pb-1">Subject</th>
                  <th className="pb-1 text-right">Conviction</th>
                  <th className="pb-1 text-right">Return</th>
                  <th className="pb-1 text-right">Held</th>
                  <th className="pb-1 text-right">Resolved</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e) => (
                  <tr key={e.id} className="border-t border-edge" data-testid={`calibration-entry-${e.id}`}>
                    <td className="py-1 font-semibold">{e.subject}</td>
                    <td className="py-1 text-right num">{e.conviction}</td>
                    <td className={`py-1 text-right num ${e.actual_return_pct >= 0 ? 'text-ok' : 'text-bad'}`}>
                      {e.actual_return_pct >= 0 ? '+' : ''}{e.actual_return_pct.toFixed(1)}%
                    </td>
                    <td className="py-1 text-right num">{e.holding_days}d</td>
                    <td className="py-1 text-right num">{e.outcome_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ledger && ledger.entries.length === 0 && !loading && (
        <div className="card p-4 text-xs text-ink-mute" data-testid="calibration-empty">
          No resolved decisions in this window yet. Fills seed entries automatically;
          outcomes resolve after positions close or the reconcile cycle marks them.
        </div>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  accent,
  extra,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'ok' | 'warn';
  extra?: React.ReactNode;
}) {
  return (
    <div className="card p-3">
      <div className="text-[10px] text-ink-mute uppercase tracking-wide">{label}</div>
      <div className="flex items-end justify-between gap-2">
        <div className={`num text-2xl font-bold mt-1 ${accent === 'ok' ? 'text-ok' : accent === 'warn' ? 'text-warn' : ''}`}>
          {value}
        </div>
        {extra}
      </div>
      {hint && <div className="text-[10px] text-ink-mute mt-1">{hint}</div>}
    </div>
  );
}

/** v0.5.8 AE-107: per-strategy outcome distribution — one tick per scored
 * outcome, chronological, up from the baseline for wins and down for
 * losses. Persisted facts only; no outcomes renders nothing. */
function OutcomeStrip({ returns }: { returns: number[] }) {
  if (!returns || returns.length === 0) return null;
  const maxAbs = Math.max(...returns.map((r) => Math.abs(r)), 0.01);
  const w = Math.min(96, returns.length * 6);
  const h = 18;
  const mid = h / 2;
  const bw = Math.max(2, Math.floor(w / returns.length) - 1);
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label={`${returns.length} scored outcomes`}
      data-testid="outcome-strip"
    >
      <line x1={0} y1={mid} x2={w} y2={mid} stroke="currentColor" strokeWidth={0.5} className="text-edge" />
      {returns.map((r, i) => {
        const x = (i / returns.length) * w;
        const len = Math.max(1.5, (Math.abs(r) / maxAbs) * (mid - 1));
        return (
          <rect
            key={i}
            x={x}
            y={r >= 0 ? mid - len : mid}
            width={bw}
            height={len}
            className={r >= 0 ? 'text-ok' : 'text-bad'}
            fill="currentColor"
          />
        );
      })}
    </svg>
  );
}
